/**
 * Site-admin Presets browse page (Phase 2 - Group D).
 *
 * Two list modes:
 *
 *   - Site-admin default: GET /group/admin/presets/?audience=shared  (we
 *     show personal too only when the operator toggles "Show all presets").
 *   - Non-admin staff:    GET /group/presets/?audience=shared (the user
 *     endpoint already visibility-gates; no toggle exposed).
 *
 * Row click navigates to `/presets/:id`. "Create shared preset" opens the
 * admin create-on-behalf modal (always uses POST /group/admin/presets/).
 *
 * Lives under PRESETS_ADMIN_ENABLED in App.tsx so this entire page is
 * never registered when the flag is off - no risk of accidental exposure
 * via direct URL.
 */
import { useMemo, useState } from "react";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonToggle,
} from "@ionic/react";
import { addOutline, gitBranchOutline, searchOutline } from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AppHeader from "../components/AppHeader";
import AuthenticationGate from "../components/AuthenticationGate";
import { Spinner } from "pymble-ui/components";
import { useMyDetails } from "../hooks/useMyDetails";
import { usePresetsList, useSharedPresetsList } from "../hooks/usePresets";
import { CreatePresetModal } from "../components/presets/CreatePresetModal";
import { ImportPresetModal } from "../components/presets/ImportPresetModal";
import {
  formatMemberCount,
  memberCountDisclosure,
} from "../components/presets/memberCount";
import type { Preset } from "../types/preset";

import "../components/presets/presets.css";

// Local-only StaffGate. Lifted from GroupBrowse so each page-level entry
// point gates independently - the route flag protects registration but the
// API still demands `active.staff` for everything.
const StaffGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isLoading, isError, user } = useMyDetails();
  if (isLoading) return <Spinner message="Checking access..." />;
  if (isError)
    return <Spinner message="Error loading user details. Please refresh." />;
  if (!user?.active?.staff) {
    return (
      <div className="ion-padding ion-text-center">
        <h2>Access Denied</h2>
        <p>This application is available to staff only.</p>
      </div>
    );
  }
  return <>{children}</>;
};

const PresetsBrowse: React.FC = () => {
  const history = useHistory();
  const { hasAdminAccess } = useMyDetails();

  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // Site-admin path uses /group/admin/presets/. Non-admin staff still see
  // shared presets via the user endpoint - the page is useful as a directory
  // even for non-admins, and we'd rather keep one render path than special-
  // case the "no admin" view into a different layout.
  const adminParams = useMemo(
    () => ({
      audience: showAll ? ("all" as const) : ("shared" as const),
      deleted_at: "exclude" as const,
      search: search.trim() || undefined,
      limit: 200,
    }),
    [showAll, search]
  );

  // Gate each query on the role we'll actually render so a non-admin's
  // visit doesn't fire a 403-bound /admin/presets/ call (and vice-versa
  // for admins, who skip the redundant /presets/ user-list). Codex review
  // round 1 Med.
  const adminQuery = usePresetsList(adminParams, hasAdminAccess);
  const sharedQuery = useSharedPresetsList(!hasAdminAccess);

  const isLoading = hasAdminAccess ? adminQuery.isLoading : sharedQuery.isLoading;

  // Apply client-side name filter on the shared view (admin path already
  // pushes `search` to the server). Cheap and avoids a second loading state
  // on every keystroke for non-admin staff. Pull the source array inside the
  // memo so the dependency list stays stable (no inline ternary in deps).
  const filteredPresets = useMemo(() => {
    const source: Preset[] = hasAdminAccess
      ? (adminQuery.data ?? [])
      : (sharedQuery.data ?? []);
    if (hasAdminAccess) return source;
    const q = search.trim().toLowerCase();
    if (!q) return source;
    return source.filter(
      (p) =>
        (p.name_short ?? "").toLowerCase().includes(q) ||
        (p.name_long ?? "").toLowerCase().includes(q)
    );
  }, [adminQuery.data, sharedQuery.data, hasAdminAccess, search]);

  // Pre-build rows so DataGrid can stay declarative. `id` is the bare uuid
  // - DataGrid requires a unique id per row and our preset.id is exactly
  // that.
  const rows = useMemo(
    () =>
      filteredPresets.map((p) => ({
        id: p.id,
        name_short: p.name_short,
        name_long: p.name_long,
        audience: p.audience,
        // A companion's roster is DERIVED from its source preset, so it is not
        // an ordinary row: nothing about it is editable here and its counts
        // mean something different. `derived_from` is the only signal, and the
        // grid is the one companion surface that was ignoring it.
        derived: !!p.derived_from,
        owner_contact_id: p.owner_contact_id,
        manager_count: p.manager_contact_ids.length,
        constituent_count: p.definition.constituent_group_ids.length,
        individual_count: p.definition.individual_contact_ids.length,
        // pymble-apis SUPPRESSES member_count to 0 whenever the member list is
        // hidden - which is how every companion starts - so the raw value is
        // not a count in that state. Keep the sortable number null rather than
        // zero (a suppressed row must not sort as the emptiest cohort on the
        // page) and carry the same words the detail page shows for display.
        member_count:
          memberCountDisclosure(p) === "visible" ? p.member_count : null,
        member_count_label: formatMemberCount(p, false),
        last_run_at: p.materialisation.last_run_at,
        last_run_status: p.materialisation.last_run_status,
      })),
    [filteredPresets]
  );

  const columns: GridColDef[] = [
    {
      field: "name_short",
      headerName: "Name",
      flex: 1.5,
      minWidth: 200,
      // renderCell only decorates - sorting and the server-side search still
      // run on the raw name value.
      renderCell: (params) => (
        <span className="preset-grid-name">
          <span className="preset-grid-name-text">
            {String(params.value ?? "")}
          </span>
          {params.row.derived && (
            <span
              className="preset-derived-badge"
              title="Derived parent group: its members are the carers of its source preset's student members, rebuilt by the materialiser."
            >
              <IonIcon icon={gitBranchOutline} aria-hidden="true" />
              Derived
            </span>
          )}
        </span>
      ),
    },
    {
      field: "audience",
      headerName: "Audience",
      width: 110,
      renderCell: (params) => (
        <span
          className={`preset-audience-badge ${
            params.value === "shared" ? "shared" : "personal"
          }`}
        >
          {String(params.value)}
        </span>
      ),
    },
    {
      field: "owner_contact_id",
      headerName: "Owner",
      width: 110,
      // The admin endpoint exposes the numeric id; the page doesn't have a
      // contact lookup yet so we show the id. Acceptable for v1 since the
      // operator can click through to the detail page for the full name
      // map. Follow-up: hydrate owner names server-side in the list payload.
    },
    {
      field: "manager_count",
      headerName: "Managers",
      width: 110,
      type: "number",
    },
    {
      field: "constituent_count",
      headerName: "Groups",
      width: 100,
      type: "number",
    },
    {
      field: "individual_count",
      headerName: "Contacts",
      width: 100,
      type: "number",
    },
    {
      field: "member_count",
      headerName: "Members",
      width: 150,
      type: "number",
      // `null` means the API did not disclose a count, so say which of the two
      // reasons applies instead of printing the suppressed zero. A companion
      // showing "0" here is indistinguishable from one that has silently
      // emptied - the failure mode this feature is most exposed to.
      renderCell: (params) =>
        params.value === null ? (
          <span className="preset-grid-muted">
            {params.row.member_count_label}
          </span>
        ) : (
          String(params.value)
        ),
    },
    {
      field: "last_run_at",
      headerName: "Last materialised",
      width: 180,
      valueGetter: (value: string | null) => {
        if (!value) return "Never";
        try {
          const dt = new Date(value);
          return dt.toLocaleString();
        } catch {
          return value;
        }
      },
    },
    {
      field: "last_run_status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => {
        if (!params.value) return <span style={{ color: "#888" }}>-</span>;
        const ok = params.value === "success";
        return (
          <span
            style={{
              color: ok ? "var(--ion-color-success, #2dd36f)" : "var(--ion-color-danger, #eb445a)",
              fontWeight: 500,
            }}
          >
            {String(params.value)}
          </span>
        );
      },
    },
  ];

  return (
    <IonPage>
      <AppHeader />
      <IonContent fullscreen>
        <AuthenticationGate>
          <StaffGate>
            <div className="preset-browse-container">
              <div className="preset-browse-header">
                <h1 className="preset-browse-title">Presets</h1>
                <div style={{ display: "flex", gap: 8 }}>
                  <IonButton
                    fill="outline"
                    color="secondary"
                    className="ion-text-capitalize"
                    onClick={() => history.push("/groups")}
                  >
                    Back to groups
                  </IonButton>
                  {hasAdminAccess && (
                    <>
                      <IonButton
                        fill="outline"
                        color="secondary"
                        className="ion-text-capitalize"
                        onClick={() => setShowImport(true)}
                      >
                        Create from list
                      </IonButton>
                      <IonButton
                        color="secondary"
                        className="ion-text-capitalize"
                        onClick={() => setShowCreate(true)}
                      >
                        <IonIcon slot="start" icon={addOutline} />
                        Create shared preset
                      </IonButton>
                    </>
                  )}
                </div>
              </div>

              <div className="preset-browse-toolbar">
                <IonItem
                  lines="none"
                  className="preset-browse-search"
                  style={{ flex: 1, maxWidth: 320 }}
                >
                  <IonIcon slot="start" icon={searchOutline} color="medium" />
                  <IonInput
                    value={search}
                    onIonInput={(e) => setSearch(e.detail.value ?? "")}
                    placeholder="Search by preset name..."
                    debounce={250}
                  />
                </IonItem>
                {hasAdminAccess && (
                  <IonItem lines="none" className="preset-browse-toggle">
                    <IonLabel>
                      Show all presets{" "}
                      <span className="preset-browse-toggle-hint">
                        (incl. personal)
                      </span>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={showAll}
                      onIonChange={(e) => setShowAll(e.detail.checked)}
                      color="secondary"
                    />
                  </IonItem>
                )}
              </div>

              <div className="preset-browse-grid" style={{ height: 600 }}>
                {isLoading ? (
                  <Spinner message="Loading presets..." />
                ) : (
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 50, page: 0 } },
                      sorting: {
                        sortModel: [{ field: "name_short", sort: "asc" }],
                      },
                    }}
                    pageSizeOptions={[25, 50, 100]}
                    onRowClick={(params) => {
                      history.push(`/presets/${params.id}`);
                    }}
                    disableRowSelectionOnClick
                  />
                )}
              </div>
            </div>
          </StaffGate>
        </AuthenticationGate>
      </IonContent>

      <IonModal
        isOpen={showCreate}
        onDidDismiss={() => setShowCreate(false)}
        backdropDismiss
        className="preset-create-modal"
      >
        <CreatePresetModal
          onDismiss={() => setShowCreate(false)}
          onCreated={(presetId) => {
            setShowCreate(false);
            history.push(`/presets/${presetId}`);
          }}
        />
      </IonModal>

      {/* Renders its own IonModal (needs onDidDismiss to reset its wizard
          state), unlike CreatePresetModal which is wrapped above. */}
      <ImportPresetModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
      />
    </IonPage>
  );
};

export default PresetsBrowse;
